import React, { useReducer, useRef } from 'react';
import { Box } from '@cp/ds/src/components/box';
import { Divider } from '@cp/ds/src/components/divider';
import { PromocodeViewModel } from '@common/model/billing/promocode';
import { promocodeApi } from '@cp/entities/promocode';
import { SerializedError } from '@reduxjs/toolkit';
import { useFormContext } from 'react-hook-form';
import { ActivatePromocodeForm, ActivatePromocodeFormRef, ActivatePromocodeLink, PromocodeActivatedMessage } from 'src/entities/promo';

enum Step {
  Link = 'link',
  Form = 'form',
  Result = 'result',
}

type State = { step: Step; promocode?: PromocodeViewModel; error?: SerializedError };

export interface PromoAtRegistrationProps {
  name: string;
}
export const PromoAtRegistration: React.FC<PromoAtRegistrationProps> = ({ name }) => {
  const [{ step, promocode, error }, dispatch] = useReducer<State, [{ type: Step; error?: SerializedError; promo?: PromocodeViewModel }]>(
    (state, action) => {
      if (state.promocode) {
        return {
          promocode: state.promocode,
          step: Step.Result,
        };
      }
      switch (action.type) {
        case Step.Form:
          return {
            step: Step.Form,
            error: action.error,
          };
        case Step.Result:
          return {
            step: Step.Result,
            promocode: action.promo,
          };
        default:
          return state;
      }
    },
    { step: Step.Link },
  );
  const [getPromocode] = promocodeApi.endpoints.getPromocode.useLazyQuery();
  const form = useRef<ActivatePromocodeFormRef>(null);
  const { register, setValue } = useFormContext();

  return (
    <Box>
      <input type="hidden" {...register(name)} />
      <Divider sx={{ visibility: step === Step.Link ? 'hidden' : 'visible' }} />
      <ActivatePromocodeLink onClick={() => dispatch({ type: Step.Form })} sx={{ visibility: step === Step.Link ? 'visible' : 'hidden' }} />
      <Box sx={{ display: step === Step.Form ? 'block' : 'none' }}>
        <ActivatePromocodeForm
          component="div"
          error={error}
          ref={form}
          submitProps={{
            onClick: async () => {
              if (!form.current) {
                return;
              }
              const valid = await form.current.trigger();
              if (valid) {
                const res = await getPromocode(form.current.getValues());
                if ('error' in res) {
                  dispatch({ type: Step.Form, error: res.error });
                } else if (typeof res.data !== 'undefined') {
                  dispatch({ type: Step.Result, promo: res.data });
                  setValue(name, res.data.id, { shouldValidate: false });
                }
              }
            },
            type: 'button',
            size: undefined,
            variant: 'secondary',
            sx: { backgroundColor: '#fff', py: 0 },
          }}
        />
      </Box>
      {promocode ? <PromocodeActivatedMessage promocode={promocode} /> : null}
    </Box>
  );
};
